import * as React from 'react';
import { CalendarIcon, Check, X } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { useIntl } from 'react-intl';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { Calendar } from 'components/v2/calendar/calendar';
import { Modal } from 'components/shared/modal';
import { SwipeModalRef } from 'components/shared/modal/mobile-modal';
import { getTimeFrameDateRange, formatDateRangeForDisplay } from './date-picker-with-presets.utils';
import { getDateFnsLocale, getWeekStartsOn } from 'shared/hooks/use-locale/date-fns-locale.utils';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';

interface DatePickerWithPresetsProps {
  value?: string; // Start date in YYYY-MM-DD format
  valueEnd?: string; // End date in YYYY-MM-DD format (for ranges)
  onValueChange?: (date: string | null) => void;
  onValueEndChange?: (date: string | null) => void;
  selectedTimeFrame?: string;
  onTimeFrameChange?: (timeFrame: string | null) => void;
  timezone: string;
  className?: string;
  disablePast?: boolean;
}

const futureTimeFrameOptions = [
  { value: 'today', labelKey: 'datePickerWithPresets.presets.today' },
  { value: 'thisWeek', labelKey: 'datePickerWithPresets.presets.thisWeek' },
  { value: 'thisMonth', labelKey: 'datePickerWithPresets.presets.thisMonth' },
  { value: 'nextWeek', labelKey: 'datePickerWithPresets.presets.nextWeek' },
  { value: 'nextMonth', labelKey: 'datePickerWithPresets.presets.nextMonth' },
  { value: 'allUpcoming', labelKey: 'datePickerWithPresets.presets.allUpcoming' },
];

const pastTimeFrameOptions = [
  { value: 'yesterday', labelKey: 'datePickerWithPresets.presets.yesterday' },
  { value: 'lastWeek', labelKey: 'datePickerWithPresets.presets.lastWeek' },
  { value: 'lastMonth', labelKey: 'datePickerWithPresets.presets.lastMonth' },
  { value: 'allPast', labelKey: 'datePickerWithPresets.presets.allPast' },
];

export const DatePickerWithPresets = ({
  value,
  valueEnd,
  onValueChange,
  onValueEndChange,
  selectedTimeFrame,
  onTimeFrameChange,
  timezone,
  className,
  disablePast = false,
}: DatePickerWithPresetsProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);
  const isSmallViewport = useIsSmallViewportWidth();
  const [open, setOpen] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const modalRef = React.useRef<SwipeModalRef>(null);

  // Close popover when clicking outside (only for desktop popover)
  React.useEffect(() => {
    if (isSmallViewport) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, isSmallViewport]);

  const handleTimeFrameSelect = React.useCallback(
    (timeFrame: string): void => {
      const range = getTimeFrameDateRange(timeFrame, timezone, getWeekStartsOn(dateFnsLocale));

      if (onTimeFrameChange) {
        onTimeFrameChange(timeFrame);
      }

      if (onValueChange) {
        onValueChange(range.start);
      }

      // For single date presets (today, yesterday), clear end date
      if (timeFrame === 'today' || timeFrame === 'yesterday') {
        if (onValueEndChange) {
          onValueEndChange(null);
        }
      } else if (onValueEndChange) {
        // For range presets, set end date
        onValueEndChange(range.end);
      }

      // On mobile, use modal's closeModal to trigger animation, otherwise close immediately
      if (isSmallViewport && modalRef.current) {
        modalRef.current.closeModal(() => setOpen(false));
      } else {
        setOpen(false);
      }
    },
    [onTimeFrameChange, onValueChange, onValueEndChange, timezone, isSmallViewport, dateFnsLocale],
  );

  const handleToggleOpen = React.useCallback((): void => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = React.useCallback((): void => {
    setOpen(false);
  }, []);

  const createTimeFrameHandler = React.useCallback(
    (timeFrame: string) => (): void => {
      handleTimeFrameSelect(timeFrame);
    },
    [handleTimeFrameSelect],
  );

  // Handle calendar range selection
  const handleCalendarSelect = React.useCallback(
    (selected: string | { from: string; to: string | null } | string[] | null): void => {
      // Calendar in range mode returns { from: string; to: string | null } | null
      if (selected && typeof selected === 'object' && !Array.isArray(selected) && 'from' in selected) {
        const range = selected as { from: string; to: string | null };

        if (onValueChange) {
          onValueChange(range.from);
        }

        if (onValueEndChange) {
          onValueEndChange(range.to || null);
        }

        // Clear the time frame when manually selecting a date
        if (onTimeFrameChange) {
          onTimeFrameChange(null);
        }

        // Keep popover open - don't auto-close
      } else if (selected === null) {
        // Clear selection
        if (onValueChange) {
          onValueChange(null);
        }
        if (onValueEndChange) {
          onValueEndChange(null);
        }
        if (onTimeFrameChange) {
          onTimeFrameChange(null);
        }
      }
    },
    [onValueChange, onValueEndChange, onTimeFrameChange],
  );

  // Get calendar selected value in range format
  const getCalendarSelected = React.useCallback((): { from: string; to: string | null } | null => {
    if (!value) {
      return null;
    }

    return {
      from: value,
      to: valueEnd || null,
    };
  }, [value, valueEnd]);

  // Get the label for the selected time frame
  const getTimeFrameLabel = (): string | null => {
    if (!selectedTimeFrame) {
      return null;
    }

    const allOptions = [...futureTimeFrameOptions, ...pastTimeFrameOptions];
    const option = allOptions.find((opt) => opt.value === selectedTimeFrame);

    if (option) {
      return formatMessage({ id: option.labelKey, defaultMessage: option.value });
    }

    return null;
  };

  // Display the date(s) in the button
  const getDisplayValue = (): string => {
    if (!value) {
      return formatMessage({
        id: 'datePickerWithPresets.selectDate',
        defaultMessage: 'Select date',
      });
    }

    return formatDateRangeForDisplay(value, valueEnd, dateFnsLocale);
  };

  // Check if we need wider button (range with different years or long ranges)
  const needsWiderButton = React.useMemo((): boolean => {
    if (!value || !valueEnd || value === valueEnd) {
      return false;
    }
    try {
      const startYear = new Date(value).getFullYear();
      const endYear = new Date(valueEnd).getFullYear();
      // Expand if years are different (e.g., "Dec 1, 2025 - Jan 15, 2026")
      return startYear !== endYear;
    } catch {
      return false;
    }
  }, [value, valueEnd]);

  const displayValue = getDisplayValue();
  const timeFrameLabel = getTimeFrameLabel();
  const showTimeFrameLabel = selectedTimeFrame && timeFrameLabel;

  // Presets content (reusable)
  const presetsContent = (
    <div className="w-full space-y-0.5">
      {/* Future date presets */}
      {futureTimeFrameOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={createTimeFrameHandler(option.value)}
          className={cn(
            'w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors flex items-center justify-between gap-2',
            selectedTimeFrame === option.value && 'bg-primary/10 text-primary font-medium',
          )}
          data-cy={`date-picker-preset-${option.value}`}
        >
          <span className="flex-1">{formatMessage({ id: option.labelKey, defaultMessage: option.value })}</span>
          {selectedTimeFrame === option.value && <Check className="h-4 w-4 text-primary shrink-0" />}
        </button>
      ))}

      {/* Divider */}
      <div className="my-2 border-t border-gray-200" />

      {/* Past date presets */}
      {pastTimeFrameOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={createTimeFrameHandler(option.value)}
          className={cn(
            'w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors flex items-center justify-between gap-2',
            selectedTimeFrame === option.value && 'bg-primary/10 text-primary font-medium',
          )}
          data-cy={`date-picker-preset-${option.value}`}
        >
          <span className="flex-1">{formatMessage({ id: option.labelKey, defaultMessage: option.value })}</span>
          {selectedTimeFrame === option.value && <Check className="h-4 w-4 text-primary shrink-0" />}
        </button>
      ))}
    </div>
  );

  // Shared content for desktop popover
  const datePickerContent = (
    <div className="flex flex-col md:flex-row">
      {/* Calendar on the left / top */}
      <div className="p-4" data-cy="date-picker-calendar">
        <Calendar
          mode="range"
          selected={getCalendarSelected()}
          onSelect={handleCalendarSelect}
          className="rounded-md border-0"
          disablePast={disablePast}
          locale={dateFnsLocale}
          todayLabel={formatMessage({
            id: 'createVisitFormV2.whenStep.calendarToday',
            defaultMessage: 'Today',
          })}
          selectedLabel={formatMessage({
            id: 'createVisitFormV2.whenStep.calendarSelected',
            defaultMessage: 'Selected',
          })}
        />
      </div>

      {/* Time frame options on the right (desktop) / below (mobile) */}
      <div className="border-t md:border-t-0 md:border-l border-gray-200 p-4 min-w-[180px] md:min-w-[200px] flex items-start">
        {presetsContent}
      </div>
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {/* Time frame label above the date picker */}
      {showTimeFrameLabel && <div className="text-sm font-medium text-gray-700">{timeFrameLabel}</div>}

      <div className="relative" ref={popoverRef}>
        <Button
          type="button"
          variant="outline"
          onClick={handleToggleOpen}
          className={cn(
            'w-full justify-start text-left font-normal h-8 pl-3 pr-4 text-base',
            needsWiderButton ? 'sm:w-[270px]' : 'sm:w-[240px]',
            !value && 'text-gray-400',
          )}
          data-cy="date-picker-button"
        >
          <CalendarIcon className="mr-2 h-5 w-5 shrink-0" />
          <span className="truncate text-sm">{displayValue}</span>
        </Button>

        {/* Desktop: Popover */}
        {open && !isSmallViewport && (
          <div
            className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg border border-gray-200 shadow-lg w-auto min-w-[320px] md:min-w-[600px]"
            data-cy="date-picker-popover"
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-2 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700 px-2">
                {formatMessage({
                  id: 'datePickerWithPresets.selectDateRange',
                  defaultMessage: 'Select date range',
                })}
              </span>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                aria-label={formatMessage({
                  id: 'datePickerWithPresets.close',
                  defaultMessage: 'Close',
                })}
                data-cy="date-picker-close"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            {datePickerContent}
          </div>
        )}
      </div>

      {/* Mobile: Modal */}
      {open && isSmallViewport && (
        <Modal ref={modalRef} onClose={handleClose} variant="normal" withExitButton>
          <div className="flex flex-col h-full max-h-[80vh]" data-cy="date-picker-modal">
            <h2 className="text-lg font-semibold mb-4 px-4 pt-4">
              {formatMessage({
                id: 'datePickerWithPresets.selectDateRange',
                defaultMessage: 'Select date range',
              })}
            </h2>
            {/* Calendar - fixed at top */}
            <div className="px-4 pb-4 flex-shrink-0" data-cy="date-picker-calendar">
              <Calendar
                mode="range"
                selected={getCalendarSelected()}
                onSelect={handleCalendarSelect}
                className="rounded-md border-0"
                disablePast={disablePast}
                locale={dateFnsLocale}
                todayLabel={formatMessage({ id: 'createVisitFormV2.whenStep.calendarToday', defaultMessage: 'Today' })}
                selectedLabel={formatMessage({
                  id: 'createVisitFormV2.whenStep.calendarSelected',
                  defaultMessage: 'Selected',
                })}
              />
            </div>
            {/* Presets - scrollable */}
            <div className="border-t border-gray-200 px-4 pb-4 flex-1 overflow-y-auto min-h-0">{presetsContent}</div>
          </div>
        </Modal>
      )}
    </div>
  );
};
